/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { useMessagingTrackingData } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import { MESSAGING_BUTTON, MESSAGING_MODAL } from 'bundles/enterprise-admin-messaging-v2/utils/eventing';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  /** Confirms the discard — closes the whole message modal, losing the draft. */
  onDiscard: () => void;
  /** Returns to the screen the admin was on before they tried to close. */
  onKeepEditing: () => void;
};

const styles = {
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    margin-top: var(--cds-spacing-300);
    width: 100%;
  `,
};

/**
 * Confirmation screen shown when the admin tries to close the modal mid-draft.
 * The modal title becomes "Discard this message?"; this renders the warning and
 * the Discard / Keep editing actions. Lives in the same Dialog so only one
 * modal is ever visible.
 */
const DiscardScreen = ({ onDiscard, onKeepEditing }: Props) => {
  const track = useTracker();
  const trackingData = useMessagingTrackingData();

  // Modal impression for the discard confirmation. "Keep editing" is a dismiss
  // the orchestrator tracks as close_modal — only the Discard action is tracked
  // here.
  React.useEffect(() => {
    track('view_modal', { ...trackingData, modal: { name: MESSAGING_MODAL.CONFIRM_DISCARD } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Track the discard confirmation, then run the close handler. */
  const handleDiscard = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.DISCARD },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.CONFIRM_DISCARD },
    });
    onDiscard();
  }, [track, trackingData, onDiscard]);

  return (
    <React.Fragment>
      <Dialog.Content>
        <Typography2 component="p" variant="bodyPrimary">
          {_t("You'll lose all your progress.")}
        </Typography2>
      </Dialog.Content>
      <Dialog.Actions>
        <div css={styles.actions}>
          <Button variant="primary" onClick={handleDiscard} data-testid="discard-message-confirm">
            {_t('Discard')}
          </Button>
          <Button variant="secondary" onClick={onKeepEditing} data-testid="discard-message-keep-editing">
            {_t('Keep editing')}
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default DiscardScreen;
